import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { apiFetch } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";

export const Route = createFileRoute("/_auth/konfiguracja/email")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.system.email-config",
			location.pathname,
		);
	},
	component: EmailConfigPage,
});

interface EmailConfig {
	delivery_method?: string;
	smtp_host?: string;
	smtp_port?: string;
	smtp_secure?: string;
	smtp_username?: string;
	password?: string;
	from_email?: string;
	from_name?: string;
}

interface SettingsResponse {
	key: string;
	value: EmailConfig | null;
}

interface TestResult {
	success: boolean;
	messageId: string | null;
	latencyMs: number;
	error: string | null;
}

function EmailConfigPage() {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const [deliveryMethod, setDeliveryMethod] = useState("oci");
	const [host, setHost] = useState("");
	const [port, setPort] = useState("587");
	const [secure, setSecure] = useState("starttls");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [fromEmail, setFromEmail] = useState("");
	const [fromName, setFromName] = useState("");
	const [initialized, setInitialized] = useState(false);

	const [testTo, setTestTo] = useState("");
	const [testChannel, setTestChannel] = useState("saved");

	const isSuperAdmin = user?.roles?.includes("super_admin");

	const settingsQuery = useQuery({
		queryKey: ["settings", "email_config"],
		queryFn: () => apiFetch<SettingsResponse>("/admin/settings/email_config"),
		enabled: !!isSuperAdmin,
	});

	if (settingsQuery.data !== undefined && !initialized) {
		const val = settingsQuery.data.value;
		if (val) {
			if (val.delivery_method) setDeliveryMethod(val.delivery_method);
			if (val.smtp_host) setHost(val.smtp_host);
			if (val.smtp_port) setPort(String(val.smtp_port));
			if (val.smtp_secure) setSecure(val.smtp_secure);
			if (val.smtp_username) setUsername(val.smtp_username);
			if (val.from_email) setFromEmail(val.from_email);
			if (val.from_name) setFromName(val.from_name);
		}
		setInitialized(true);
	}

	const saveMutation = useMutation({
		mutationFn: () => {
			// Puste haslo = zachowaj dotychczasowe (backend merguje pola wrazliwe).
			const payload: EmailConfig = {
				delivery_method: deliveryMethod,
				smtp_host: host.trim(),
				smtp_port: port.trim(),
				smtp_secure: secure,
				smtp_username: username.trim(),
				password,
				from_email: fromEmail.trim(),
				from_name: fromName.trim(),
			};
			return apiFetch("/admin/settings/email_config", {
				method: "PUT",
				body: JSON.stringify({ value: payload, encrypted: true }),
			});
		},
		onSuccess: () => {
			setPassword("");
			queryClient.invalidateQueries({ queryKey: ["settings", "email_config"] });
		},
	});

	const testMutation = useMutation({
		mutationFn: () =>
			apiFetch<TestResult>("/admin/settings/email/test", {
				method: "POST",
				body: JSON.stringify({
					to: testTo.trim(),
					channel: testChannel === "saved" ? null : testChannel,
				}),
			}),
	});

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	const savedPassword = settingsQuery.data?.value?.password;

	return (
		<div className="p-6 max-w-2xl space-y-6 max-md:p-3 max-md:space-y-3">
			<h1 className="text-2xl font-semibold max-md:hidden">Konfiguracja e-mail</h1>

			<Card>
				<CardHeader>
					<CardTitle>Kanał wysyłki</CardTitle>
					<CardDescription>
						OCI Email Delivery ma wspólny dzienny limit API dla całego tenancy. Tryb auto wysyła
						przez OCI, a przy błędzie (np. wyczerpany limit) automatycznie przechodzi na SMTP, który
						ma osobną pulę.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="deliveryMethod">Tryb</Label>
						<Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
							<SelectTrigger id="deliveryMethod">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="auto">Auto — OCI, przy błędzie SMTP</SelectItem>
								<SelectItem value="oci">Tylko OCI Email Delivery</SelectItem>
								<SelectItem value="smtp">Tylko SMTP</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Serwer SMTP</CardTitle>
					<CardDescription>
						Dane logowania do serwera SMTP (np. OCI: smtp.email.eu-frankfurt-1.oci.oraclecloud.com,
						port 587, STARTTLS). Hasło jest szyfrowane w bazie.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
						<div className="md:col-span-2 space-y-2">
							<Label htmlFor="smtpHost">Host</Label>
							<Input
								id="smtpHost"
								value={host}
								onChange={(e) => setHost(e.target.value)}
								placeholder="smtp.email.eu-frankfurt-1.oci.oraclecloud.com"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="smtpPort">Port</Label>
							<Input
								id="smtpPort"
								inputMode="numeric"
								value={port}
								onChange={(e) => setPort(e.target.value)}
							/>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="smtpSecure">Szyfrowanie</Label>
						<Select value={secure} onValueChange={setSecure}>
							<SelectTrigger id="smtpSecure">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
								<SelectItem value="tls">TLS (port 465)</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<Label htmlFor="smtpUsername">Użytkownik</Label>
						<Input
							id="smtpUsername"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
							autoComplete="off"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="smtpPassword">Hasło</Label>
						<Input
							id="smtpPassword"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder={savedPassword ? "Zostaw puste, aby zachować obecne" : ""}
							autoComplete="new-password"
						/>
						{savedPassword && (
							// Celowo bez podgladu wartosci — nawet zamaskowane 4+4 znaki
							// hasla SMTP to za duzo (API key w ai.tsx to inna klasa sekretu).
							<p className="text-xs text-muted-foreground">
								Hasło jest zapisane. Zostaw pole puste, aby je zachować.
							</p>
						)}
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						<div className="space-y-2">
							<Label htmlFor="fromEmail">Adres nadawcy</Label>
							<Input
								id="fromEmail"
								value={fromEmail}
								onChange={(e) => setFromEmail(e.target.value)}
								placeholder="noreply@farmaster.pl"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="fromName">Nazwa nadawcy</Label>
							<Input
								id="fromName"
								value={fromName}
								onChange={(e) => setFromName(e.target.value)}
								placeholder="Farmaster"
							/>
						</div>
					</div>

					<div className="flex items-center gap-3 max-md:flex-wrap">
						<Button
							onClick={() => saveMutation.mutate()}
							disabled={saveMutation.isPending}
							className="max-md:h-11 max-md:w-full"
						>
							{saveMutation.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								"Zapisz ustawienia"
							)}
						</Button>
						{saveMutation.isSuccess && (
							<span className="text-sm text-muted-foreground inline-flex items-center gap-1">
								<CheckCircle2 className="h-4 w-4 text-primary" /> Zapisano
							</span>
						)}
						{saveMutation.isError && (
							<span className="text-sm text-destructive inline-flex items-center gap-1">
								<XCircle className="h-4 w-4" /> Błąd zapisu
							</span>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Test wysyłki</CardTitle>
					<CardDescription>
						Wysyła testową wiadomość wskazanym kanałem. Wynik trafia też do logu e-maili.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
						<div className="md:col-span-2 space-y-2">
							<Label htmlFor="testTo">Adres odbiorcy</Label>
							<Input
								id="testTo"
								value={testTo}
								onChange={(e) => setTestTo(e.target.value)}
								placeholder={user?.email ?? ""}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="testChannel">Kanał</Label>
							<Select value={testChannel} onValueChange={setTestChannel}>
								<SelectTrigger id="testChannel">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="saved">Zapisany tryb</SelectItem>
									<SelectItem value="auto">Auto</SelectItem>
									<SelectItem value="oci">OCI</SelectItem>
									<SelectItem value="smtp">SMTP</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>

					<div className="flex items-center gap-3 max-md:flex-wrap">
						<Button
							variant="outline"
							onClick={() => testMutation.mutate()}
							disabled={testMutation.isPending || !testTo.includes("@")}
							className="max-md:h-11 max-md:w-full"
						>
							{testMutation.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								"Wyślij testowy e-mail"
							)}
						</Button>
						{testMutation.data &&
							(testMutation.data.success ? (
								<span className="text-sm text-muted-foreground inline-flex items-center gap-1">
									<CheckCircle2 className="h-4 w-4 text-primary" /> Wysłano (
									{testMutation.data.latencyMs} ms)
								</span>
							) : (
								<span className="text-sm text-destructive inline-flex items-center gap-1 max-md:items-start max-md:break-words max-md:min-w-0">
									<XCircle className="h-4 w-4 shrink-0" /> {testMutation.data.error}
								</span>
							))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
